import { Schema } from "effect";
import { AgenticTraceContext, AgenticTraceContextData, type AgenticTraceContextInput } from "../agentic-correlation.ts";
import { plainIdentityData } from "../identity-input.ts";
import { ImmutableExecutionSource, ImmutableExecutionSourceInput } from "../runtime/immutable-source.ts";
import { HostedRuntimeNamespace } from "./http.ts";

/** Preserve the trusted caller-owned identity without runtime or tracing IO. */
export const correlationProps = (correlation?: Readonly<AgenticTraceContextInput>) =>
  correlation ? { agenticCorrelation: correlation } : {};

const ParentScope = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value =>
  value.length > 0 && value.length <= 512 && value.trim() === value && !/[\x00-\x1f\x7f]/.test(value))))();
const ParentMount = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value => value.length <= 1024 &&
  !/[\x00-\x1f\x7f\\%]/.test(value) && (value === "" || (value.startsWith("/") && !value.endsWith("/") && !value.includes("//"))) &&
  !value.split("/").some(segment => segment === "." || segment === ".."))))();
export const HostedExecutionContext = /* @__PURE__ */ (() => Schema.Struct({
  parentScope: ParentScope, parentMountPath: ParentMount, parentDepth: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 3 })),
  runtimeNamespaceId: Schema.optional(HostedRuntimeNamespace), immutableSource: Schema.optional(ImmutableExecutionSource), agenticCorrelation: Schema.optional(AgenticTraceContext)
}).check(Schema.makeFilter(context => {
  const namespace = context.runtimeNamespaceId;
  if (namespace === undefined) return true;
  const scopePrefix = `${namespace}:scope:`, mountPrefix = `/.workerstation/runtime/${namespace}`;
  return context.parentScope.startsWith(scopePrefix) && context.parentScope.length > scopePrefix.length &&
    (context.parentMountPath === mountPrefix || context.parentMountPath.startsWith(`${mountPrefix}/`)) &&
    (context.agenticCorrelation?.namespaceId === undefined || context.agenticCorrelation.namespaceId === namespace);
}, { message: "Hosted-test execution authority must belong to its runtime namespace." })))();
export type HostedExecutionContext = typeof HostedExecutionContext.Type;
export const HostedExecutionContextInput = /* @__PURE__ */ (() => plainIdentityData(
  ["parentScope", "parentMountPath", "parentDepth", "runtimeNamespaceId", "immutableSource", "agenticCorrelation"], "Hosted-test execution context"
).pipe(Schema.decodeTo(Schema.Struct({
  ...HostedExecutionContext.fields,
  immutableSource: Schema.optional(ImmutableExecutionSourceInput), agenticCorrelation: Schema.optional(AgenticTraceContextData)
})), Schema.decodeTo(HostedExecutionContext)))();
/** Freeze the fresh records returned by decoding the authority contract. */
export const freezeHostedContext = (context: HostedExecutionContext): HostedExecutionContext => {
  Object.freeze(context.immutableSource);
  Object.freeze(context.agenticCorrelation);
  return Object.freeze(context);
};
